import { useCallback, useEffect, useMemo } from "react";
import { place } from "streamplace";
import storage from "../storage";
import {
  getStreamplaceStoreFromContext,
  useStreamplaceStore,
} from "./streamplace-store";
import { usePossiblyUnauthedPDSAgent } from "./xrpc";

export interface BrandingAsset {
  key: string;
  mimeType: string;
  url?: string; // URL for images
  data?: string; // inline data for text, or base64 for images
  width?: number; // image width in pixels
  height?: number; // image height in pixels
}

// helper to convert blob to base64
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const PropsInHeader = [
  "siteTitle",
  "siteDescription",
  "primaryColor",
  "accentColor",
  "defaultStreamer",
  "mainLogo",
  "favicon",
  "sidebarBg",
  "legalLinks",
];

export function getMetaContent(key: string): BrandingAsset | null {
  if (typeof window === "undefined" || !window.document) return null;
  const meta = document.querySelector(`meta[name="internal-brand:${key}`);
  if (meta && meta.getAttribute("content")) {
    let content = meta.getAttribute("content");
    if (content) return JSON.parse(content) as BrandingAsset;
  }

  return null;
}

// Every branding asset the node wrote into the page (pkg/linking puts one
// internal-brand:<key> meta per key, small images inline as data URLs), or
// null off the web / behind the dev proxy. This is the whole of getBranding
// as of the moment the page was rendered, so the store can be filled from
// it before any request goes out.
function getAllMetaBranding(): Record<string, BrandingAsset> | null {
  if (typeof window === "undefined" || !window.document) return null;
  const metas = document.querySelectorAll('meta[name^="internal-brand:"]');
  if (!metas.length) return null;
  const out: Record<string, BrandingAsset> = {};
  metas.forEach((meta) => {
    const key = meta.getAttribute("name")!.slice("internal-brand:".length);
    const content = meta.getAttribute("content");
    if (!content) return;
    try {
      out[key] = JSON.parse(content) as BrandingAsset;
    } catch {
      // a malformed tag is just a missing key
    }
  });
  return Object.keys(out).length ? out : null;
}

// One branding fetch at a time: the auto-fetch on boot and a caller's own
// call can overlap, and each used to walk every image asset again.
let brandingInflight: Promise<void> | null = null;

// hook to fetch broadcaster DID (unauthenticated)
export function useFetchBroadcasterDID() {
  const streamplaceAgent = usePossiblyUnauthedPDSAgent();
  const store = getStreamplaceStoreFromContext();

  // prefetch from meta records, if on web
  useEffect(() => {
    if (typeof window !== "undefined" && window.document) {
      try {
        const metaRecords = PropsInHeader.reduce(
          (acc, key) => {
            const meta = document.querySelector(
              `meta[name="internal-brand:${key}`,
            );
            // hrmmmmmmmmmmmm
            if (meta && meta.getAttribute("content")) {
              let content = meta.getAttribute("content");
              if (content) acc[key] = JSON.parse(content) as BrandingAsset;
            }
            return acc;
          },
          {} as Record<string, BrandingAsset>,
        );

        console.log("Found meta records for broadcaster DID:", metaRecords);
        // filter out all non-text values, can get on second fetch?
        for (const key of Object.keys(metaRecords)) {
          if (metaRecords[key].mimeType != "text/plain") {
            delete metaRecords[key];
          }
        }
      } catch (e) {
        console.warn("Failed to parse broadcaster DID from meta tags", e);
      }
    }
  }, []);

  return useCallback(async () => {
    try {
      if (!streamplaceAgent) {
        throw new Error("Streamplace agent not available");
      }
      const result = await streamplaceAgent.client.call(
        place.stream.broadcast.getBroadcaster,
      );
      store.setState({ broadcasterDID: result.broadcaster });
      if (result.server) {
        store.setState({ serverDID: result.server });
      }
    } catch (err) {
      console.error("Failed to fetch broadcaster DID:", err);
    }
  }, [streamplaceAgent, store]);
}

// hook to fetch client-facing env config from the server
export function useFetchEnvConfig() {
  const streamplaceAgent = usePossiblyUnauthedPDSAgent();
  const store = getStreamplaceStoreFromContext();

  return useCallback(async () => {
    try {
      if (!streamplaceAgent) {
        throw new Error("Streamplace agent not available");
      }
      const result = await streamplaceAgent.client.call(
        place.stream.config.getEnv,
      );
      if (result.playbackWorkerUrl) {
        store.setState({ playbackWorkerUrl: result.playbackWorkerUrl });
      }
      store.setState({ gamesEnabled: result.gamesEnabled ?? false });
    } catch (err) {
      console.error("Failed to fetch env config:", err);
    }
  }, [streamplaceAgent, store]);
}

// hook to fetch branding data from the server
export function useFetchBranding() {
  const streamplaceAgent = usePossiblyUnauthedPDSAgent();
  const broadcasterDID = useStreamplaceStore((state) => state.broadcasterDID);
  const url = useStreamplaceStore((state) => state.url);
  const store = getStreamplaceStoreFromContext();

  return useCallback(
    async ({ force = true } = {}) => {
      if (!broadcasterDID) return;
      if (brandingInflight && !force) return brandingInflight;
      const run = fetchBrandingOnce({
        force,
        broadcasterDID,
        streamplaceAgent,
        url,
        store,
      }).finally(() => {
        if (brandingInflight === run) brandingInflight = null;
      });
      brandingInflight = run;
      return run;
    },
    [broadcasterDID, streamplaceAgent, url, store],
  );
}

async function fetchBrandingOnce({
  force,
  broadcasterDID,
  streamplaceAgent,
  url,
  store,
}: {
  force: boolean;
  broadcasterDID: string;
  streamplaceAgent: ReturnType<typeof usePossiblyUnauthedPDSAgent>;
  url: string | undefined | null;
  store: ReturnType<typeof getStreamplaceStoreFromContext>;
}) {
  try {
    store.setState({ brandingLoading: true });

    // The page itself carries the branding (web): fill the store from
    // it now, so nothing renders unbranded and no request is needed for
    // the first paint. Only assets too big to inline (URL-only) are
    // fetched, in parallel. An explicit refresh (the admin screen after
    // an upload) still asks the node.
    const fromPage = getAllMetaBranding();
    if (fromPage && !force) {
      if (!store.getState().branding) {
        store.setState({ branding: fromPage });
      }
      const pending = Object.values(fromPage).filter((a) => !a.data && a.url);
      await Promise.all(
        pending.map(async (asset) => {
          try {
            asset.data = await fetchAssetDataUrl(url, asset);
          } catch (e) {
            console.warn("branding asset fetch failed", asset.key, e);
          }
        }),
      );
      store.setState({
        branding: { ...fromPage },
        brandingLoading: false,
        brandingError: null,
      });
      return;
    }

    // check localStorage first
    const cacheKey = `branding:${broadcasterDID}`;
    const cached = await storage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        const fresh = Date.now() - parsed.timestamp < 60 * 60 * 1000;
        if (!force && fresh) {
          store.setState({
            branding: parsed.data,
            brandingLoading: false,
            brandingError: null,
          });
          return;
        }
        // Paint what we had last time right away, then refresh: the
        // alternative is a flash of default branding on every cold start
        // (no server-injected meta on native or behind the dev proxy).
        if (parsed.data && !store.getState().branding) {
          store.setState({ branding: parsed.data });
        }
      } catch (e) {
        // invalid cache, continue to fetch
        console.warn("Invalid branding cache, refetching", e);
      }
    }

    // fetch branding metadata from server
    if (!streamplaceAgent) {
      throw new Error("Streamplace agent not available");
    }
    const res = await streamplaceAgent.client.call(
      place.stream.branding.getBranding,
      {
        broadcaster: broadcasterDID as any,
      },
    );
    const assets = res.assets;

    // convert assets array to keyed object and fetch blob data — the
    // images all at once, not one after another
    const brandingMap: Record<string, BrandingAsset> = {};
    for (const asset of assets) {
      brandingMap[asset.key] = { ...asset };
    }
    await Promise.all(
      assets
        .filter((asset) => !asset.data && asset.url)
        .map(async (asset) => {
          brandingMap[asset.key].data = await fetchAssetDataUrl(url, asset);
        }),
    );

    // cache in localStorage
    storage.setItem(
      cacheKey,
      JSON.stringify({
        timestamp: Date.now(),
        data: brandingMap,
      }),
    );

    store.setState({
      branding: brandingMap,
      brandingLoading: false,
      brandingError: null,
    });
  } catch (err: any) {
    console.error("Failed to fetch branding:", err);
    store.setState({
      brandingLoading: false,
      brandingError: err.message || "Failed to fetch branding",
    });
  }
}

// Fetches an image asset and returns it as a data URL. FileReader stamps
// the data URI with the blob's served Content-Type, which some nodes return
// as application/octet-stream — browsers refuse to render that as a favicon,
// so it flashes then vanishes. Prefer the asset's declared mimeType so the
// icon (and other image assets) actually display.
async function fetchAssetDataUrl(
  url: string | undefined | null,
  asset: { url?: string; mimeType?: string },
): Promise<string> {
  const blobRes = await fetch(`${url}${asset.url}`);
  const blob = await blobRes.blob();
  const dataUrl = await blobToBase64(blob);
  return asset.mimeType
    ? dataUrl.replace(/^data:[^;,]*/, `data:${asset.mimeType}`)
    : dataUrl;
}

// hook to get a specific branding asset by key
export function useBrandingAsset(key: string): BrandingAsset | undefined {
  // The hook runs unconditionally; the fallbacks are plain values.
  const fromStore = useStreamplaceStore((state) => state.branding?.[key]);
  return fromStore || getMetaContent(key) || undefined;
}

/**
 * App shell shape (branding key appLayout). "social" is the timeline-style
 * shell: no top bar, the viewer's avatar atop the nav rail, a fixed-width
 * feed column and upstream social-app nav icons.
 */
export function useAppLayout(): "classic" | "social" {
  const asset = useBrandingAsset("appLayout");
  return asset?.data === "social" ? "social" : "classic";
}

export function useSocialShell(): boolean {
  return useAppLayout() === "social";
}

/** The stream page as a post card beside live chat: streamLayout=card, which
 *  the social shell implies. */
export function useCardStreamLayout(): boolean {
  const layout = useBrandingAsset("streamLayout")?.data;
  const social = useSocialShell();
  return layout === "card" || social;
}

/** What the app calls the social network (branding key networkName). */
export function useNetworkName(): string {
  return useBrandingAsset("networkName")?.data?.trim() || "Bluesky";
}

/**
 * Where a user's profile link goes (branding key networkProfileUrl, with
 * {handle} and {did} placeholders); the Bluesky profile page by default.
 */
export function useNetworkProfileUrl(): (author: {
  handle?: string;
  did?: string;
}) => string {
  const template =
    useBrandingAsset("networkProfileUrl")?.data?.trim() ||
    "https://bsky.app/profile/{handle}";
  return (author) =>
    template
      .replace(
        "{handle}",
        encodeURIComponent(author.handle || author.did || ""),
      )
      .replace("{did}", encodeURIComponent(author.did || ""));
}

/** Example handle in the login form's empty field (branding key
 *  loginPlaceholder), or undefined for the app's own. */
export function useLoginPlaceholder(): string | undefined {
  return useBrandingAsset("loginPlaceholder")?.data?.trim() || undefined;
}

// convenience hook for main logo
export function useMainLogo(): string | undefined {
  const asset = useBrandingAsset("mainLogo");
  return asset?.data;
}

// convenience hook for favicon
export function useFavicon(): string | undefined {
  const asset = useBrandingAsset("favicon");
  return asset?.data;
}

// convenience hook for site title
export function useSiteTitle(): string {
  const asset = useBrandingAsset("siteTitle");
  return asset?.data || "My Streamplace Node";
}

// convenience hook for site description
export function useSiteDescription(): string {
  const asset = useBrandingAsset("siteDescription");
  return asset?.data || "Live streaming platform";
}

// convenience hook for primary color
export function usePrimaryColor(): string {
  const asset = useBrandingAsset("primaryColor");
  return asset?.data || "#6366f1"; // token-ok: branding config default value
}

// convenience hook for accent color
export function useAccentColor(): string {
  const asset = useBrandingAsset("accentColor");
  return asset?.data || "#8b5cf6"; // token-ok: branding config default value
}

// convenience hook for default streamer
export function useDefaultStreamer(): string | undefined {
  const asset = useBrandingAsset("defaultStreamer");
  return asset?.data || undefined;
}

/**
 * The default video (branding key defaultVideo), parsed to the video page's
 * route params: the node's front door while set, over defaultStreamer.
 */
export function useDefaultVideo(): { user: string; tid: string } | undefined {
  const asset = useBrandingAsset("defaultVideo");
  return useMemo(() => parseDefaultVideo(asset?.data), [asset?.data]);
}

/**
 * Accepts a video's at:// URI (at://did/place.stream.video/rkey) or its page
 * path (<handle-or-did>/video/<rkey>, with or without a leading slash or a
 * full URL around it). Undefined for anything else.
 */
export function parseDefaultVideo(
  value?: string,
): { user: string; tid: string } | undefined {
  if (!value) return undefined;
  let s = value.trim();
  const at = s.match(/^at:\/\/([^/]+)\/place\.stream\.video\/([^/?#]+)$/);
  if (at) return { user: at[1], tid: at[2] };
  if (/^https?:\/\//.test(s)) {
    try {
      s = new URL(s).pathname;
    } catch {
      return undefined;
    }
  }
  const path = s.match(/^\/?([^/]+)\/video\/([^/?#]+)\/?$/);
  if (path) return { user: path[1], tid: path[2] };
  return undefined;
}

// convenience hook for sidebar background image
export function useSidebarBackgroundImage(): BrandingAsset | undefined {
  return useBrandingAsset("sidebarBackgroundImage");
}

// The illustration for "nothing is playing": the front page when nobody is
// streaming, and a video without a thumbnail. A data URL when the node
// uploaded one (branding key offlineImage); undefined means the default art.
export function useOfflineImageUri(): string | undefined {
  const data = useBrandingAsset("offlineImage")?.data;
  return data && data.startsWith("data:") ? data : undefined;
}

// convenience hook for legal links
export function useLegalLinks(): { text: string; url: string }[] {
  const asset = useBrandingAsset("legalLinks");
  if (!asset?.data) {
    return [];
  }
  try {
    return JSON.parse(asset.data);
  } catch {
    return [];
  }
}

// hook to auto-fetch branding when broadcaster changes
export function useBrandingAutoFetch() {
  const fetchBranding = useFetchBranding();
  const broadcasterDID = useStreamplaceStore((state) => state.broadcasterDID);

  useEffect(() => {
    if (broadcasterDID) {
      fetchBranding({ force: false });
    }
  }, [broadcasterDID, fetchBranding]);
}

// Whether the first paint can be branded: the store has branding (fetched or
// hydrated from cache), the fetch failed (nothing more will arrive), or the
// page carries server-injected branding meta (web) that the asset hooks read
// directly. Until then the shell holds its blank frame so the default mark
// and title never flash before the node's own.
export function useBrandingSettled(): boolean {
  return useStreamplaceStore(
    (s) =>
      s.branding !== null ||
      s.brandingError !== null ||
      getMetaContent("siteTitle") !== null,
  );
}
